import {
  AfterViewInit, ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {Prestazione} from "../../models/prestazione.model";
import moment from "moment/moment";
import {ActivatedRoute} from "@angular/router";
import {DayService} from "../../services/day.service";
import {initFlowbite} from "flowbite";
import {NgClass, NgForOf, NgStyle} from "@angular/common";
import {PrestazioneService} from "../../services/prestazione.service";
import {environment} from "../../../environments/environment";
import {SlotService} from "../../services/slot.service";
import {catchError, of, switchMap, throwError} from "rxjs";
import Swal from "sweetalert2";

@Component({
  selector: 'app-admin-calendar',
  standalone: true,
  imports: [
    NgForOf,
    NgStyle,
    NgClass
  ],
  templateUrl: './admin-calendar.component.html',
  styleUrl: './admin-calendar.component.css'
})
export class AdminCalendarComponent implements OnInit, AfterViewInit, OnDestroy {

  // Variabili d'ambiente
  date = moment(); // Data corrente
  oraApertua: number = environment.oraApertua;
  oraChiusura: number = environment.oraChiusura;

  // Dati passati dal componente padre
  @Input() prestazione!: Prestazione; // Prestazione selezionata

  // Variabili per i dati del frontend
  titlePrestazione = 'Nome'; // Titolo della prestazione
  stanza = '(descrizione)'; // Descrizione della stanza
  timePosition: number = 0; // Posizione del tempo
  slotDurationMinute: number = 20; // Durata dello slot in minuti (default 20)
  isHighlightRed: boolean = false; // Flag per evidenziare in rosso
  timerInterval: any; // Intervallo del timer

  // Campi calcolati
  slots: any[] = []; // Slot del giorno
  allSlots: any[] = []; // Slot di tutta la settimana
  events: any[] = []; // Eventi del calendario

  // Costruttore del componente
  constructor(
    private prestazioneService: PrestazioneService, // Servizio per la gestione delle prestazioni
    private slotService: SlotService,
    private cd: ChangeDetectorRef, // Riferimento al ChangeDetector
    private route: ActivatedRoute, // Rotta attiva
    private dayService: DayService // Servizio per la gestione dei giorni
  ) { }

  // Metodo chiamato all'inizializzazione del componente
  ngOnInit() {

    // Sottoscrizione ai parametri della rotta
    this.route.paramMap.subscribe(params => {
      if (params.get('day')) {
        this.dayService.changeDay(params.get('day'));
        this.date = moment(params.get('day'));
        this.getSlots();
        this.getPrestazione();
      }
    });

    // Inizializzazione di Flowbite
    initFlowbite();
    // Impostazione delle variabili del calendario in base alla prestazione
    this.setCalendarVariablesBasedOnPerformance();
    // Aggiornamento della posizione del tempo ogni minuto
    this.updateTimePositionEveryMinute();

    // Recupero gli slot per la giornata
    this.getSlots();
    // Recupero la prestazione
    this.getPrestazione();
  }

  // Metodo chiamato dopo l'inizializzazione del componente
  ngAfterViewInit(): void {
    // Aggiornamento della posizione del tempo
    this.updateTimePosition();
  }

  // Metodo chiamato alla distruzione del componente
  ngOnDestroy() {
    // Pulizia dell'intervallo del timer
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  /*
  Chiamate a BE tramite service
  */

  // Filtro gli slot totali con solo quelli che mi servono
  filterSlots(){
    moment.locale('it');
    const giorno = this.date.format('dddd');
    this.slots = this.allSlots.filter(slot => slot.day === giorno);
  }

  // Recupero gli slot da BE
  getSlots() {
    if(this.allSlots.length === 0){
      this.slotService.getAllSlot(this.prestazione._id)
        .pipe(
          catchError(error => {
            console.error("Errore nel recupero degli slot", error);
            return throwError(error);
          })
        )
        .subscribe((response) => {
          this.allSlots = response.slot;
          this.filterSlots();
        });
    }

    this.filterSlots();
  }

  // Recupero gli slot da BE
  getPrestazione() {
    this.prestazioneService.show(this.prestazione._id)
      .pipe(
        catchError(error => {
          console.error("Errore nel recupero degli slot", error);
          return throwError(error);
        })
      )
      .subscribe((response) => {
        this.prestazione = response.prestazione;
      });
  }

  // Imposta le variabili del calendario in base alla prestazione
  setCalendarVariablesBasedOnPerformance() {
    // Imposta la durata dello slot in base alla durata della prestazione, o usa il valore predefinito di 20 minuti se non è specificata
    this.slotDurationMinute = this.prestazione?.durata || 20;

    // Imposta il titolo della prestazione in base al nome della prestazione, o usa il valore predefinito 'Nome' se non è specificato
    this.titlePrestazione = this.prestazione?.nome || 'Nome';

    // Imposta la stanza in base alla descrizione della prestazione, o usa il valore predefinito '(descrizione)' se non è specificata
    this.stanza = this.prestazione?.descrizione || '(descrizione)';
  }

  /*
    Eventi per azioni a FE
  */

  // Blocco lo slot selezionato
  onSlotClick(slot: any) {
    // Creiamo il momento corrispondente
    const date = moment(`${this.date.format('YYYY-MM-DD')} ${slot.startTime}`).locale('en');
    const blockedSlot = date.toString();

    if (this.isBlocked(slot)) {
      // Se lo slot è bloccato, rimuovilo
      if (this.prestazione && this.prestazione.blockedSlots) {
        this.prestazioneService.removeBlockedSlots(this.prestazione._id, [blockedSlot]).subscribe({
          next: () => {
            // Rimuovi lo slot bloccato dall'array nel componente
            // @ts-ignore
            const index = this.prestazione.blockedSlots.indexOf(blockedSlot);
            if (index > -1) {
              // @ts-ignore
              this.prestazione.blockedSlots.splice(index, 1);
            }
            // Forza Angular a rilevare i cambiamenti
            this.cd.detectChanges();
          },
          error: (error) => {
            console.error("Errore nella rimozione dello slot bloccato", error);
          }
        });
      }
    } else {
      // Se lo slot non è bloccato, aggiungilo
      if (this.prestazione && this.prestazione.blockedSlots) {
        this.prestazione.blockedSlots.push(blockedSlot);
        this.prestazioneService.addBlockedSlots(this.prestazione._id, [blockedSlot]).subscribe({
          next: () => {
            // Forza Angular a rilevare i cambiamenti
            this.cd.detectChanges();
          },
          error: (error) => {
            console.error("Errore nell'aggiunta dello slot bloccato", error);
          }
        });
      }
    }
  }

  // Blocco tutti gli slot della prestazione per la data odierna
  blockAllSlots() {
    if (this.prestazione) {
        const blockedSlots = this.slots.map(slot => {
          const date = moment(`${this.date.format('YYYY-MM-DD')} ${slot.startTime}`).locale('en');
          return date.toString();
      });
      this.prestazioneService.addBlockedSlots(this.prestazione._id, blockedSlots).subscribe({
        next: () => {
          this.prestazione.blockedSlots = blockedSlots;
          this.cd.detectChanges();
        },
        error: (error) => {
          console.error("Errore nell'aggiunta degli slot bloccati", error);
        }
      });
    }
  }

  // Sblotto tutti gli slot della prestazione per la data odierna
  unblockAllSlots() {
    if (this.prestazione && this.prestazione.blockedSlots) {
      const blockedSlots = [...this.prestazione.blockedSlots];
      this.prestazioneService.removeBlockedSlots(this.prestazione._id, blockedSlots).subscribe({
        next: () => {
          this.prestazione.blockedSlots = [];
          this.cd.detectChanges();
        },
        error: (error) => {
          console.error("Errore nella rimozione degli slot bloccati", error);
        }
      });
    }
  }

  // Blocco la prestazione per un periodo
  blockPeriodo(){
    Swal.fire({
      title: `Chiudi ${this.prestazione.nome } per il periodo selezionato`,
      html: `
      <div style="text-align: left;">
        <label for="dataInizio">Data Inizio:</label>
        <input type="date" id="dataInizio" class="input mt-2" />
        <label for="dataFine">Data Fine:</label>
        <input type="date" id="dataFine" class="input mt-2" />
      </div>
    `,
      showCancelButton: true,
      confirmButtonText: 'Conferma',
      cancelButtonText: 'Annulla',
      confirmButtonColor: "#FF2E77",
      cancelButtonColor: "#050A30",
      preConfirm: () => {
        const dataInizio = (document.getElementById('dataInizio') as HTMLInputElement).value;
        const dataFine = (document.getElementById('dataFine') as HTMLInputElement).value;

        if (!dataInizio || !dataFine) {
          Swal.showValidationMessage('Per favore, inserisci entrambe le date (Data Inizio e Data Fine).');
          return null; // Ritorna null per impedire il passaggio alla fase "then" se i campi sono vuoti
        }

        return { dataInizio, dataFine };
      }
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        console.log('Data Inizio:', result.value.dataInizio);
        console.log('Data Fine:', result.value.dataFine);

        this.prestazioneService.blockPeriodoPrestazione(this.prestazione._id, result.value.dataInizio, result.value.dataFine)
          .pipe(
            switchMap((response) => {
              console.log("Periodo bloccato", response);

              Swal.fire({
                icon: 'success',
                title: `${this.prestazione.nome } bloccato`,
                timer: 1000
              });

              this.getPrestazione();
              return of(response);
            })
          )
          .subscribe({
            error: (error) => {
              console.error("Errore nella chiusira", error);

              Swal.fire({
                icon: 'error',
                title: 'Ops...',
                text: "Errore nell'inserimento del blocco del periodo",
                confirmButtonColor: '#050A30'
              });

            }
          });

      }
    });

  }

  /*
 Calcolo rendering a FE
 */

  // Aggiorna la posizione del time-indicator
  updateTimePosition() {
    const currentTime = new Date();
    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    const totalMinutes = hours * 60 + minutes;
    const startMinutes = this.oraApertua * 60;
    let adjustedMinutes = totalMinutes - startMinutes;
    const intervalDuration = (this.oraChiusura - this.oraApertua) * 60;

    if (adjustedMinutes < 0) {
      adjustedMinutes = 0;
    } else if (adjustedMinutes > intervalDuration) {
      adjustedMinutes = intervalDuration;
    }

    // todo porcheria per la linea rossa (dovrebbe prendere in automatico l'altezza)
    switch (this.prestazione.descrizione) {
      case 'Lettino Alta':
        this.timePosition = 1.91837838 * adjustedMinutes;
        break;
      case 'Doccia':
      case 'Viso':
        this.timePosition = 1.944 * adjustedMinutes;
        break;
      // Sia lettini a bassa che a Led
      default:
        this.timePosition = 1.94366667 * adjustedMinutes;
        break;
    }
  }

  // Aggiorna la posizione del tempo ogni minuto
  updateTimePositionEveryMinute(): void {
    // Imposta un intervallo per chiamare la funzione updateTimePosition ogni minuto
    this.timerInterval = setInterval(() => {
      Promise.resolve().then(() => this.updateTimePosition());
    }, 60000); // 60000 millisecondi equivalgono a 1 minuto
  }

  // Controllo se uno slot è bloccato
  isBlocked(slot: any): boolean {
    if (!this.prestazione?.blockedSlots) {
      return false;
    }

    // Creiamo il momento corrispondente
    const date = moment(`${this.date.format('YYYY-MM-DD')} ${slot.startTime}`).locale('en');

    // Confrontiamo con gli slot bloccati della prestazione
    return this.prestazione.blockedSlots.includes(date.toString());
  }
}
